import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import CommonLoading from "../loader/CommonLoading";
import LoadMoreButton from "../shared/LoadMoreButton";
import { FaUserPlus, FaTimes } from "react-icons/fa";

import { 
  getSentFollowRequestsAction, 
  clearSentFollowRequestsAction,
  withdrawFollowRequestAction
} from "../../redux/actions/followRequestActions";
import { useToast } from "../../contexts/ToastContext";

const SentFollowRequestsModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [withdrawingRequestId, setWithdrawingRequestId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const sortOrder = "desc"; // Fixed sort order (newest first)
  
  // TODO: Replace with actual Redux state when backend is ready
  const sentFollowRequests = useSelector((state) => state.user?.sentFollowRequests || []);
  const totalSentFollowRequests = useSelector((state) => state.user?.totalSentFollowRequests || 0);

  const LIMIT = 25;

  useEffect(() => {
    if (isOpen) {
      const fetchRequests = async () => {
        setLoading(true);
        await dispatch(getSentFollowRequestsAction(null, LIMIT, 0));
        setLoading(false);
      };
      fetchRequests();
    }

    return () => {
      if (!isOpen) {
        dispatch(clearSentFollowRequestsAction());
      }
    };
  }, [dispatch, LIMIT, isOpen]);

  const handleUserClick = useCallback((user) => {
    const profileUrl = user?.profileUrl || user?._id;
    if (profileUrl) {
      onClose();
      navigate(`/profile/${profileUrl}`);
    }
  }, [navigate, onClose]);

  const handleLoadMore = useCallback(() => {
    setIsLoadMoreLoading(true);
    const currentLength = sentFollowRequests?.length || 0;
    dispatch(getSentFollowRequestsAction(null, LIMIT, currentLength)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, LIMIT, sentFollowRequests?.length]);

  const handleWithdraw = useCallback(async (requestId) => {
    setWithdrawingRequestId(requestId);
    try {
      const result = await dispatch(withdrawFollowRequestAction(requestId));
      if (result?.success) {
        showSuccess("Follow request withdrawn successfully");
        // Refresh the list to update counts
        await dispatch(getSentFollowRequestsAction(null, LIMIT, 0));
      } else {
        showError(result?.error || "Failed to withdraw request");
      }
    } catch (error) {
      showError("Error withdrawing request");
    } finally {
      setWithdrawingRequestId(null);
    }
  }, [dispatch, showSuccess, showError, LIMIT]);

  // Sort requests by newest/oldest
  const sortedRequests = useMemo(() => {
    if (!sentFollowRequests || sentFollowRequests.length === 0) return [];
    
    const sorted = [...sentFollowRequests].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
    
    return sorted;
  }, [sentFollowRequests, sortOrder]);


  const displayedRequests = useMemo(() => {
    return sortedRequests.map((request) => {
      const recipient = request.recipient || request.user;
      if (!recipient) return null;

      return (
        <div 
          key={request._id}
          className="bg-white border rounded-md w-full shadow-2xl shadow-[#f2f5fc] relative hover:shadow-lg hover:border-primary-300 transition-all duration-300"
        >
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="hover:bg-gray-50 rounded-md p-3 -m-3 transition-colors">
              <div className="flex items-start justify-between gap-3">
                {/* Left: User info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <img
                      src={recipient.avatar || "/default-avatar.png"}
                      alt="Avatar"
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-0.5 cursor-pointer"
                      onClick={() => handleUserClick(recipient)}
                    />
                    <div className="flex-1 min-w-0">
                      <div 
                        className="cursor-pointer mb-1"
                        onClick={() => handleUserClick(recipient)}
                      >
                        <h4 className="font-semibold text-gray-800 font-heading text-base truncate">
                          {recipient.name}
                        </h4>
                      </div>
                      
                      {/* Request time */}
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-600 font-medium font-flex-regular">
                          Sent: {new Date(request.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right: Status badge and withdraw button */}
                <div className="flex-shrink-0 flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                    request.status === "accepted"
                      ? "text-green-800 bg-green-200"
                      : request.status === "denied"
                      ? "text-action bg-action/10"
                      : "text-yellow-700 bg-yellow-100"
                  }`}>
                    {request.status === "accepted" ? "Accepted" : request.status === "denied" ? "Denied" : "Pending"}
                  </span>
                  {request.status === "pending" && (
                    <button
                      onClick={() => handleWithdraw(request._id)}
                      disabled={withdrawingRequestId === request._id}
                      className="px-3 py-1 text-xs font-medium text-action bg-action/10 hover:bg-action/20 rounded-full whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {withdrawingRequestId === request._id ? "Withdrawing..." : "Withdraw"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }).filter(Boolean);
  }, [sortedRequests, handleUserClick, handleWithdraw, withdrawingRequestId]);

  const shouldShowLoadMore = useMemo(() => {
    if (!sentFollowRequests || sentFollowRequests.length === 0) return false;
    const currentCount = sentFollowRequests?.length || 0;
    const totalCount = totalSentFollowRequests || 0;
    return currentCount < totalCount && totalCount > LIMIT;
  }, [sentFollowRequests, totalSentFollowRequests, LIMIT]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-4">
                  <div className="flex items-center gap-3">
                    <FaUserPlus className="text-2xl text-primary-600" />
                    <Dialog.Title className="text-lg font-semibold text-gray-700">
                      Sent Follow Requests
                    </Dialog.Title>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FaTimes className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                  {loading ? (
                    <div className="flex justify-center items-center py-8">
                      <CommonLoading />
                    </div>
                  ) : sentFollowRequests?.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 gap-3">
                        {displayedRequests}
                      </div>
                      
                      {shouldShowLoadMore && (
                        <div className="mt-4">
                          <LoadMoreButton
                            onClick={handleLoadMore}
                            isLoading={isLoadMoreLoading}
                            text="Load More Requests"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <div className="flex justify-center items-center flex-col">
                        <FaUserPlus className="text-6xl text-gray-300 mb-4" />
                        <p className="text-gray-500 py-4 font-roboto-flex">
                          You haven't sent any follow requests yet.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SentFollowRequestsModal;
